import SendIcon from "@mui/icons-material/Send"
import { Switch, Typography } from "@mui/material"
import { KeyboardEventHandler, ReactElement, useState } from "react"
import { useForm } from "react-hook-form"

import { useGetUserData } from "../../../hooks/useGetUserData"
import { formatDateTime } from "../../../utils/general/generalUtils"
import AppButton from "../../buttons/appButton"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import { COMMENT_REG } from "../../popUps/dnoResponse/dnoResponseForm"
import DisplayIf from "../../wrappers/displayIf"

interface IGenericCreateCommentsComponentProps {
    mutate: () => void
    isLoading: boolean
    createComment: (comment: string) => Promise<void>
    markAsRead?: (read: boolean, resetSwitchOnFail: () => void) => Promise<void>
    toggleDeviceRead?: (read: boolean) => Promise<void>
    lastReadBy?: string
    lastReadAt?: string
    isApplication?: boolean
    isThreadUnread?: boolean
}

/**
 * Generic component for adding a comment to a `GenericCommentsCard` component.
 * The functional component should be passed as an argument in `GenericCommentsCard` props
 */
export const GenericCreateCommentsComponent = ({
    mutate,
    isLoading,
    createComment,
    markAsRead,
    toggleDeviceRead,
    lastReadBy,
    lastReadAt,
    isApplication,
    isThreadUnread = false,
}: IGenericCreateCommentsComponentProps): ReactElement => {
    const { control, handleSubmit, watch, reset } = useForm({
        shouldUnregister: true,
    })

    const {
        isManuApproved,
        isGenAssessor,
        isDemandAssessor,
        isInstaller,
        isDnoApproved,
    } = useGetUserData()

    // Generic for comment read status (device and applications)
    const [isToggled, setIsToggled] = useState(isThreadUnread)
    const submitResponse = (): void => {
        const comment = watch(COMMENT_REG)
        createComment(comment)
            .then(() => reset())
            .then(() => mutate())
    }

    const handleKeyPress: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key === "Enter" && !event.altKey) {
            event.preventDefault()
            submitResponse()
        }
    }

    const handleApplicationToggleChange = async (): Promise<void> => {
        const previousToggleState = isToggled
        const newToggleState = !isToggled
        setIsToggled(newToggleState)
        // Call markAsRead with the new toggle state
        if (markAsRead) {
            await markAsRead(!newToggleState, () => {
                setIsToggled(previousToggleState)
            })

            // Reload the component by triggering the mutate function
            mutate()
        }
    }

    const handleDeviceToggleChange = async (): Promise<void> => {
        const newToggleState = !isToggled
        setIsToggled(newToggleState)

        // Call markAsRead with the new toggle state
        if (toggleDeviceRead) {
            await toggleDeviceRead(!newToggleState)

            // Reload the component by triggering the mutate function
            mutate()
        }
    }

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            <DisplayIf condition={!!lastReadBy}>
                <Typography
                    variant="body2"
                    style={{
                        color: "gray",
                        marginBottom: "8px",
                        fontStyle: "italic",
                    }}
                >
                    {`Last read by: ${lastReadBy}, ${formatDateTime(
                        lastReadAt
                    )}`}
                </Typography>
            </DisplayIf>
            <ValidatedTextField
                control={control}
                required
                registerName={COMMENT_REG}
                placeholder="Enter a new comment for this device..."
                multiline
                handleKeyPress={handleKeyPress}
            />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "8px",
                }}
            >
                {(isManuApproved || isGenAssessor || isDemandAssessor) && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            style={{ marginRight: "8px" }}
                        >
                            Read
                        </Typography>
                        <Switch
                            checked={isToggled}
                            onChange={handleDeviceToggleChange}
                            color="primary"
                        />
                        <Typography
                            variant="body2"
                            style={{ marginLeft: "8px" }}
                        >
                            Unread
                        </Typography>
                    </div>
                )}
                {isApplication && (isInstaller || isDnoApproved) && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            style={{ marginRight: "8px" }}
                        >
                            Read
                        </Typography>
                        <Switch
                            checked={isToggled}
                            onChange={handleApplicationToggleChange}
                            color="primary"
                        />
                        <Typography
                            variant="body2"
                            style={{ marginLeft: "8px" }}
                        >
                            Unread
                        </Typography>
                    </div>
                )}

                <AppButton
                    onClick={handleSubmit(submitResponse)}
                    disabled={isLoading}
                    endIcon={<SendIcon />}
                >
                    Add Comment
                </AppButton>
            </div>
        </div>
    )
}

// Remove the default export while adding toggle functionality for device comments
GenericCreateCommentsComponent.defaultProps = {
    markAsRead: undefined,
    toggleDeviceRead: undefined,
    lastReadBy: undefined,
    lastReadAt: undefined,
    isApplication: true,
    isThreadUnread: false,
}
